// Recent binders service - retrieval, normalization, and unified list logic
// Port of public/recent-binders.js (data logic only, no DOM manipulation)

import { extractSourceInfo, extractBinderSourceInfo, toShortPath } from '../logic/url-parser.js';
import {
  MAX_RECENT_BINDERS,
  MAX_MY_BINDERS,
  RECENT_BINDERS_KEY,
  PINNED_BINDERS_KEY,
  MY_BINDERS_KEY,
  SELECTED_CARDS_CACHE_PREFIX,
  HOME_BINDER_LIST_KEY,
} from '../logic/config.js';
import { getStorageItem, setStorageItem, removeStorageItem } from './storage-manager.js';
import {
  saveBindersToCloud,
  getOpenTradesForUser,
  loadRecentBindersFromCloud,
} from './user-data.js';
import { isUserAnonymous } from '../stores/auth.svelte.js';
import {
  getClaimedBinders,
  saveClaimedBinders,
  getPinnedBindersFromApi,
  savePinnedBindersToApi,
} from './groups-api.js';
import {
  processClaimBinder,
  processUnclaimBinder,
  isBinderClaimed as isBinderClaimedCheck,
  buildUnifiedBinderList,
} from '../logic/my-binders-logic.js';

/**
 * Normalize any binder URL to BinderBrowser short URL format.
 * This ensures we always store and link to BinderBrowser URLs, not source URLs.
 * @param {string} url - Any binder URL (source URL, full binderbrowser URL, or short code)
 * @returns {string} - Normalized short URL path (e.g., "/mfb.abc123")
 */
export function normalizeToShortUrl(url) {
  if (!url) return url;

  // Trade URLs - use as-is (both /trades/ format and legacy formats)
  if (url.startsWith('/trades/') || url.startsWith('/trade/') || url.startsWith('#/trade/')) {
    return url;
  }

  // Already a short URL format without leading slash (e.g., mfb.abc123)
  const shortUrlMatch = url.match(/^([a-z]+)\.(.+)$/);
  if (shortUrlMatch) {
    return `/${url}`;
  }

  // Already a relative path with short URL format (e.g., /mfb.abc123)
  if (url.startsWith('/') && url.match(/^\/[a-z]+\./)) {
    return url;
  }

  // Check if it's a full URL (binderbrowser.com or localhost)
  try {
    const urlObj = new URL(url);
    const pathMatch = urlObj.pathname.match(/^\/+([a-z]+\..+)$/);
    if (pathMatch) {
      return `/${pathMatch[1]}`;
    }
  } catch {
    // Not a valid URL, continue to source URL parsing
  }

  // Parse as a source URL (moxfield.com, archidekt.com, etc.)
  const sourceInfo = extractSourceInfo(url);
  const shortPath = sourceInfo && toShortPath(sourceInfo);
  if (shortPath) {
    return shortPath;
  }

  // Fallback: return original URL
  console.warn('[Recent Binders] Could not normalize URL:', url);
  return url;
}

// --- Local storage helpers ---

/** Anonymous users' recent binders, validated; a malformed entry is cleared. */
export function getLocalBinders() {
  try {
    const binders = getStorageItem(RECENT_BINDERS_KEY);
    if (!binders) return [];

    if (Array.isArray(binders) && binders.every((b) => typeof b === 'object' && b.url && b.name)) {
      return binders;
    }

    removeStorageItem(RECENT_BINDERS_KEY);
    return [];
  } catch (error) {
    console.error('[Recent Binders] Error getting binders from storage:', error);
    removeStorageItem(RECENT_BINDERS_KEY);
    return [];
  }
}

// --- Pinned binders (Firestore for signed-in, localStorage for anonymous) ---

export async function getPinnedBinders() {
  if (!isUserAnonymous()) {
    try {
      return await getPinnedBindersFromApi();
    } catch (e) {
      console.warn('[Recent Binders] Failed to load pinned binders:', e);
      return [];
    }
  } else {
    return getStorageItem(PINNED_BINDERS_KEY) || [];
  }
}

async function savePinnedBinders(urls) {
  if (!isUserAnonymous()) {
    try {
      await savePinnedBindersToApi(urls);
    } catch (e) {
      console.warn('[Recent Binders] Failed to save pinned binders:', e);
    }
  } else {
    setStorageItem(PINNED_BINDERS_KEY, urls);
  }
}

export async function pinBinder(url) {
  const pinned = await getPinnedBinders();
  const normalized = normalizeToShortUrl(url);
  if (!pinned.includes(normalized)) {
    pinned.unshift(normalized);
    await savePinnedBinders(pinned);
  }
}

export async function unpinBinder(url) {
  const pinned = await getPinnedBinders();
  const normalized = normalizeToShortUrl(url);
  const updated = pinned.filter((u) => u !== normalized);
  await savePinnedBinders(updated);
}

// --- My binders (claimed binders, max 3) ---

export async function getMyBinders() {
  if (!isUserAnonymous()) {
    try {
      return await getClaimedBinders();
    } catch (e) {
      console.warn('[MyBinders] Failed to load from cloud:', e);
      return [];
    }
  } else {
    return getStorageItem(MY_BINDERS_KEY) || [];
  }
}

export async function saveMyBinders(binders) {
  if (!isUserAnonymous()) {
    try {
      await saveClaimedBinders(binders);
    } catch (e) {
      console.warn('[MyBinders] Failed to save to cloud:', e);
    }
  } else {
    setStorageItem(MY_BINDERS_KEY, binders);
  }
}

/**
 * Claim a binder as the user's own (max 3).
 * Removes from recents and unpins if pinned.
 * Auto-shares to groups with verified ownership.
 * @returns {Promise<boolean>} true if claimed, false if rejected
 */
export async function claimBinder({ url, name, owner }) {
  const myBinders = await getMyBinders();
  const result = processClaimBinder(myBinders, { url, name, owner }, MAX_MY_BINDERS);

  if (!result.success) {
    if (result.reason === 'already_claimed') {
      console.log('[MyBinders] Already claimed:', normalizeToShortUrl(url));
    } else {
      console.warn('[MyBinders] Max binders reached (' + MAX_MY_BINDERS + ')');
    }
    return false;
  }

  await saveMyBinders(result.updatedBinders);

  const normalized = normalizeToShortUrl(url);

  // Remove from recents and unpin
  await removeRecentBinder(normalized);
  await unpinBinder(normalized);

  console.log('[MyBinders] Claimed binder:', normalized);
  return true;
}

/**
 * Unclaim a binder, adding it back to recents.
 */
export async function unclaimBinder(url) {
  const myBinders = await getMyBinders();
  const { updatedBinders, removedBinder } = processUnclaimBinder(myBinders, url);
  await saveMyBinders(updatedBinders);

  // Add back to recents
  if (removedBinder) {
    await addRecentBinder({
      url: removedBinder.url,
      name: removedBinder.name,
      owner: removedBinder.owner,
    });
  }

  console.log('[MyBinders] Unclaimed binder:', normalizeToShortUrl(url));
}

/**
 * Check if a URL is claimed as the user's binder.
 */
export async function isMyBinder(url) {
  const myBinders = await getMyBinders();
  return isBinderClaimedCheck(myBinders, url);
}

// --- Binder retrieval ---

/**
 * Get binders from the appropriate source (cloud for signed-in, local for anonymous).
 * Normalizes legacy URLs and deduplicates.
 */
export async function getRecentBinders() {
  const signedIn = !isUserAnonymous();
  let binders;
  if (signedIn) {
    binders = (await loadRecentBindersFromCloud()) || [];
  } else {
    binders = getLocalBinders();
  }

  // Normalize any legacy URLs and deduplicate
  let dirty = false;
  for (const b of binders) {
    if (b.url) {
      const normalized = normalizeToShortUrl(b.url);
      if (normalized !== b.url) {
        console.log('[Recent Binders] Fixed legacy URL:', b.url, '->', normalized);
        b.url = normalized;
        dirty = true;
      }
    }
  }

  // Deduplicate by normalized URL (keep most recently accessed)
  const seen = new Set();
  const before = binders.length;
  binders = binders.filter((b) => {
    const key = (b.url || '').toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (binders.length < before) {
    console.log('[Recent Binders] Removed', before - binders.length, 'duplicate entries');
    dirty = true;
  }

  if (dirty) {
    console.log('[Recent Binders] Saving cleaned binder list');
    if (signedIn) {
      saveBindersToCloud(binders).catch((err) => {
        console.warn('[Recent Binders] Failed to save cleaned binders to cloud:', err);
      });
    } else {
      setStorageItem(RECENT_BINDERS_KEY, binders);
    }
  }

  return binders;
}

/**
 * Add a binder to the recent binders list.
 */
export async function addRecentBinder(binderInfo) {
  if (!binderInfo || !binderInfo.url || !binderInfo.name) {
    return;
  }

  const normalizedUrl = normalizeToShortUrl(binderInfo.url);

  // Skip non-binder URLs (e.g., /groups, /search) that can't be normalized,
  // and cubes: a cube is a Project's source, never a binder.
  if (!extractBinderSourceInfo(normalizedUrl)) {
    return;
  }

  const signedIn = !isUserAnonymous();
  let recentBinders = await getRecentBinders();

  // Remove duplicates based on normalized URL
  recentBinders = recentBinders.filter(
    (b) => normalizeToShortUrl(b.url).toLowerCase() !== normalizedUrl.toLowerCase()
  );

  // Add lastAccessed timestamp with normalized URL
  const binderWithTime = {
    ...binderInfo,
    url: normalizedUrl,
    lastAccessed: Date.now(),
  };
  delete binderWithTime.originalUrl;

  recentBinders.unshift(binderWithTime);
  recentBinders.splice(MAX_RECENT_BINDERS);

  if (signedIn) {
    saveBindersToCloud(recentBinders).catch((err) => {
      console.warn('[Recent Binders] Failed to save to cloud:', err);
    });
  } else {
    setStorageItem(RECENT_BINDERS_KEY, recentBinders);
  }
}

/**
 * Remove a binder from the recent binders list.
 * @param {string} url - The binder URL to remove
 */
export async function removeRecentBinder(url) {
  if (!url) return;
  const normalizedUrl = normalizeToShortUrl(url).toLowerCase();
  const signedIn = !isUserAnonymous();
  let recentBinders = await getRecentBinders();

  recentBinders = recentBinders.filter(
    (b) => normalizeToShortUrl(b.url).toLowerCase() !== normalizedUrl
  );

  // Clear cached selected cards
  const sourceInfo = extractSourceInfo(url);
  if (sourceInfo?.source && sourceInfo?.type && sourceInfo?.id) {
    const cacheKey = `${SELECTED_CARDS_CACHE_PREFIX}${sourceInfo.source}_${sourceInfo.type}_${sourceInfo.id}`;
    removeStorageItem(cacheKey);
  }

  if (signedIn) {
    saveBindersToCloud(recentBinders).catch((err) => {
      console.warn('[Recent Binders] Failed to save removal to cloud:', err);
    });
  } else {
    setStorageItem(RECENT_BINDERS_KEY, recentBinders);
  }
}

/**
 * Get unified list of binders: trade binders + starred binders + recent binders.
 * Sorted by: active trades (by update) -> starred (by view) -> other recents (by view)
 */
export async function getUnifiedBinderList() {
  // Three independent sources (Firestore user doc, Firestore trades query,
  // pinned_binders_api): start them together so the home page waits for the
  // slowest one, not the sum of all three. Each already degrades to [] on error.
  const [recentBinders, openTrades, pinnedUrls] = await Promise.all([
    getRecentBinders(),
    getOpenTradesForUser(),
    getPinnedBinders(),
  ]);

  return buildUnifiedBinderList({
    recentBinders,
    openTrades,
    pinnedUrls,
    maxBinders: MAX_RECENT_BINDERS,
  });
}

// --- Home page list cache (signed-in) ---
// The last unified list, for painting the home page before the network
// answers. One slot tagged with its uid, so a different account on the same
// browser never sees it and entries never pile up.

/**
 * @param {string} uid
 * @returns {Array | null}
 */
export function getCachedBinderList(uid) {
  const cached = getStorageItem(HOME_BINDER_LIST_KEY);
  if (cached?.uid !== uid || !Array.isArray(cached.binders)) return null;
  return cached.binders;
}

/**
 * @param {string} uid
 * @param {Array} binders
 */
export function cacheBinderList(uid, binders) {
  setStorageItem(HOME_BINDER_LIST_KEY, { uid, binders });
}
